<template>
  <div class="columns">
    <div class="column is-8 is-offset-2">
      <!-- this wraps a custom stepper graphic and the steps together -->
      <multi-stage-form
        :current-step="currentStep"
        @stepsCounted="(num) => numOfSteps = num"
      >
        <!-- the stepper goes in the stepper slot -->
        <template v-slot:stepper>
          <business-form-stepper
            class="business-stepper"
            :current-step="currentStep"
            @stepTo="moveToStep"
          >
            <font-awesome-icon
              icon="check"
            />
          </business-form-stepper>
        </template>
        <StepBusiness
          :value="form.business"
          :title="$t('business_listing.stepper.business')"
          step-class="form-section"
          :is-outer-step="isFirstOrLastStep"
          @next="next"
          @prev="prev"
        />
        <StepAddress
          :value="form.address"
          :title="$t('business_listing.stepper.address')"
          step-class="form-section"
          :is-outer-step="isFirstOrLastStep"
          @next="next"
          @prev="prev"
        />
        <StepContact
          :value="form.contact"
          :title="$t('business_listing.stepper.contact')"
          step-class="form-section"
          :is-outer-step="isFirstOrLastStep"
          :parse-phone="parsePhone"
          @next="next"
          @prev="prev"
        />
      </multi-stage-form>

      <Button-Group
        :disabled="showLoading"
        :success-save="successSave"
        :failed-save="failedSave"
        :check-input="checkInput"
        @save="saveprofile"
        @cancel="cancel"
      />
    </div>
  </div>
</template>

<script>

import { SECONDS_IN_MS } from '@/helpers'
import StepBusiness from '../components/BusinessDetails/StepBusiness'
import StepContact from '../components/BusinessDetails/StepContact'
import StepAddress from '../components/BusinessDetails/StepAddress'
import BusinessFormStepper from '../components/BusinessFormStepper'
import MultiStageForm from '../components/MultiStageForm'
import ButtonGroup from '../components/BusinessDetails/ButtonGroup'
import businessProfileMixin from '../mixins/business-profile'

export default {

  name: 'BusinessProfileForm',

  components: {
    StepBusiness,
    StepContact,
    StepAddress,
    BusinessFormStepper,
    MultiStageForm,
    ButtonGroup
  },

  mixins: [
    businessProfileMixin
  ],

  data () {
    return {
      currentStep: 0,
      numOfSteps: 0,
      stepProgress: [],
      showLoading: false,
      checkInput: false,
      failedSave: false,
      successSave: false,
      parsePhone: false,

      form: {
        accountProfileId: null,
        contact: {
          phoneNumber: '',
          email: '',
          webUrl: '',
          hasError: false
        },
        business: {
          firstName: '',
          lastName: '',
          businessName: '',
          businessDescription: '',
          hasError: false
        },
        address: {
          addressLine1: '',
          addressLine2: '',
          countryCode: '',
          state: '',
          city: '',
          province: '',
          postCode: '',
          hideAddress: false,
          hasError: false
        }
      }
    }
  },
  computed: {
    isFirstOrLastStep () {
      return {
        first: this.currentStep === 0,
        last: this.currentStep === this.numOfSteps
      }
    }
  },
  watch: {
    businessProfile: function () {
      this.loadProfile()
    }
  },
  methods: {
    moveToStep (step) {
      this.currentStep = step
    },
    next ($v) {
      $v.$touch()
      if ($v.$invalid) {
        // do nothing yet
      } else {
        $v.$reset()
        this.stepProgress[this.currentStep] = true
        this.currentStep++
      }
    },
    prev () {
      this.currentStep--
    },
    checkErrors () {
      if (this.form.business.hasError === true) {
        this.currentStep = 0
        return false
      }

      if (this.form.address.hasError === true) {
        this.currentStep = 1
        return false
      }

      if (this.form.contact.hasError === true) {
        this.currentStep = 2
        return false
      }
      return true
    },

    clearFlags () {
      this.checkInput = false
      this.successSave = false
      this.failedSave = false
    },

    async saveprofile () {
      this.parsePhone = !this.parsePhone
      await new Promise(function (resolve, reject) {
        setTimeout(() => resolve('done'), 500)
      })

      this.clearFlags()
      if (!this.checkErrors()) {
        this.checkInput = true
        setTimeout(this.clearFlags(), SECONDS_IN_MS(3))
        return false
      }
      this.showLoading = true
      const result = await this.saveBusinessProfile(this.form)

      if (result) {
        this.showLoading = false
        this.successSave = true
        setTimeout(() => { this.successSave = false }, SECONDS_IN_MS(4))
      } else {
        this.showLoading = false
        this.failedSave = true
        setTimeout(() => { this.failedSave = false }, SECONDS_IN_MS(4))
      }

      return true
    },
    cancel () {
      this.loadProfile()
    },
    loadProfile () {
      var parsedData = {
        accountProfileId: null,
        contact: {
          phoneNumber: '',
          email: '',
          webUrl: ''
        },
        business: {
          firstName: '',
          lastName: '',
          businessName: '',
          businessDescription: ''
        },
        address: {
          addressLine1: '',
          addressLine2: '',
          countryCode: '',
          state: '',
          city: '',
          province: '',
          postCode: '',
          hideAddress: false
        }
      }

      var businessProfile = this.$apolloData.data.businessProfile

      if (businessProfile && businessProfile.accountProfileId) {
        parsedData.accountProfileId = businessProfile.accountProfileId
        // Contact information
        parsedData.contact.phoneNumber = (businessProfile.phoneNumber != null) ? businessProfile.phoneNumber : ''
        parsedData.contact.email = (businessProfile.email != null) ? businessProfile.email : ''
        parsedData.contact.webUrl = (businessProfile.websiteUrl != null) ? businessProfile.websiteUrl : ''

        // Business Data
        parsedData.business.firstName = (businessProfile.firstName != null) ? businessProfile.firstName : ''
        parsedData.business.lastName = (businessProfile.lastName != null) ? businessProfile.lastName : ''
        parsedData.business.businessName = (businessProfile.businessName != null) ? businessProfile.businessName : ''
        parsedData.business.businessDescription = (businessProfile.businessDescription != null) ? businessProfile.businessDescription : ''

        var address = businessProfile.address

        // Address
        parsedData.address.addressLine1 = (address.address1 != null) ? address.address1 : ''
        parsedData.address.addressLine2 = (address.address2 != null) ? address.address2 : ''
        parsedData.address.countryCode = (address.countryCode != null) ? address.countryCode : ''
        parsedData.address.city = (address.city != null) ? address.city : ''
        parsedData.address.postCode = (address.postalCode != null) ? address.postalCode : ''

        if (address.countryCode === 'US') {
          parsedData.address.state = (address.stateOrProvince != null) ? address.stateOrProvince : ''
          parsedData.address.province = ''
        } else {
          parsedData.address.state = ''
          parsedData.address.province = address.county
        }
        parsedData.address.hideAddress = (address.hideAddress !== null) ? address.hideAddress : false
      }

      this.form = Object.assign({}, this.form, parsedData)

      return true
    }
  }
}
</script>

<style lang="scss" scoped>
  @import '../../sass/_variables.scss';

  .black {
    color:black;
  }

  article {
    max-width: 500px;
    margin: auto;
  }

  .custom-stepper {
    margin-bottom: 25px;
    margin-top: 20px;
  }

</style>

<style lang="scss">
.form-section {
  padding: 20px 15px;
  background-color: white;
  color:black;
  margin: 5px auto;

  &.form-instruction {
    font-size: 1.2em;
  }

  &.dark {
    // background-color: $primary-blue;
    background-color: var(--primary-color);
    color: white;
  }
}

</style>

<style lang="scss">
@import '../../sass/_variables.scss';
  .time-picker-group {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 10px;
    min-height: 35px;

    .time-picker-pair {
      display: flex;
      justify-content: center;
      align-items: center;
      position: relative;
      width: 300px;
      text-align: center;

      .time-picker {
        width: inherit;
        input.display-time {
          border-radius: 4px;
          text-align: center;
          width: 135px;
        }
        .dropdown {
          ul li.active {
            background-color: var(--sidebar-bg-color);
            &:hover {
              background-color: var(--sidebar-bg-color);
            }
          }
        }
      }
    }
  }

  @media only screen and (max-width: $break-small) {
    .time-picker-group {
      flex-direction: column;
      > div:first-child {
        align-self: baseline;
        margin: 5px 0;
      }
    }
  }
</style>

<style type="text/css">

  .shake {
    animation-duration: .4s;
    animation-iteration-count: 1;
    animation-name: shake !important;
    animation-timing-function: linear;
    transform-origin: 50% 50%;
  }
</style>
